<template>
    <div class="manage-guest-groups">

        <!-- Guest Phone Number Prompt -->
        <div v-if="msg.type === 'guest_group_flow' && isGuestPhonePrompt"
             class="gg-text-message gg-phone-card">

            <div class="gg-phone-question"
                 v-html="formatPhonePrompt(responseText)">
            </div>

            <div class="gg-phone-row">
                <Dropdown v-model="phoneForm.countryCode"
                          :options="countryCodeList"
                          optionValue="value"
                          optionLabel="text"
                          placeholder="Code"
                          filter
                          :disabled="disabled"
                          class="gg-country-dropdown"
                          @change="onGuestCountryCodeChange" />

                <input v-model="phoneForm.phoneNumber"
                       type="text"
                       inputmode="numeric"
                       class="gg-phone-input"
                       :disabled="disabled"
                       :maxlength="phoneLength === 10 ? phoneLength + 4 : phoneLength"
                       placeholder="Phone number"
                       @input="onGuestPhoneInput" />
            </div>

            <div v-if="phoneError" class="gg-phone-error">
                {{ phoneError }}
            </div>

            <button type="button"
                    class="gg-btn gg-btn--primary gg-phone-submit"
                    :disabled="disabled"
                    @click="submitGuestPhone">
                Continue
            </button>
        </div>

        <!-- Guest Group Preview -->
        <div v-else-if="msg.type === 'guest_group_preview'" class="gg-summary-card">
            <div class="gg-summary-header">

                <div>
                    <div class="gg-header-title">Guest Group Summary</div>
                    <div class="gg-header-subtitle">{{ responseText || 'Please review before saving' }}</div>
                </div>
            </div>

            <div class="gg-summary-body">
                <div class="gg-row">
                    <span class="gg-label">Group Name</span>
                    <span class="gg-value">{{ prefilled.groupName || '—' }}</span>
                </div>

                <div v-if="prefilledGuests.length" class="gg-preview-guests">
                    <div v-for="(guest, index) in prefilledGuests" :key="guest.guestId || guest.id || index" class="gg-preview-guest">
                        {{ guest.fullName || '—' }}<span v-if="guest.email"> — {{ guest.email }}</span>
                    </div>
                </div>
                <div v-else class="gg-empty-inline">No guests added.</div>
            </div>

            <div class="gg-actions gg-actions--stacked">
                <button type="button" class="gg-btn gg-btn--primary" :disabled="disabled" @click="confirmGroup">
                    Confirm &amp; Save
                </button>
                <button type="button" class="gg-btn gg-btn--cancel" :disabled="disabled" @click="$emit('cancel')">
                    Cancel
                </button>
            </div>
        </div>

        <!-- Guest Group Created / Failed -->
        <div v-else-if="msg.type === 'guest_group_created' || msg.type === 'guest_group_failed'" class="ticket-preview-card">
            <div class="ticket-preview-header" :class="{ 'gg-error-header': isError }">

                <div>
                    <h4>{{ isError ? 'Not Saved' : 'Guest Group Saved!' }}</h4>
                    <p>{{ isError ? 'Something went wrong.' : 'Your guest group has been saved.' }}</p>
                </div>
            </div>

            <div class="ticket-preview-body">
                <div class="ticket-preview-row">
                    <span class="tp-label">Group</span>
                    <span class="tp-value">{{ guestGroupData.groupName || '—' }}</span>
                </div>
                <div class="ticket-preview-row">
                    <span class="tp-label">Guests</span>
                    <span class="tp-value">{{ resultGuests.length }}</span>
                </div>
                <div v-if="isError" class="ticket-preview-error">
                    <span class="tp-err-icon">⚠️</span>
                    <p><strong>Error:</strong> {{ guestGroupData.error || 'Please try again.' }}</p>
                </div>
            </div>

            <div class="ticket-preview-footer">
                <span> {{ formatMMDDYYYY(currentDate) }}</span>
                <span :style="{ color: isError ? '#dc2626' : '#16a34a' }">{{ isError ? 'Not Saved' : 'Saved' }}</span>
            </div>
        </div>

        <!-- Guest Group List -->
        <div v-else-if="msg.type === 'view_guest_groups'" class="ticket-preview-card">
            <div class="ticket-preview-header">

                <div><h4>{{ guestGroupData.heading || 'Your Guest Groups' }}</h4></div>
                <span v-if="groups.length" class="ticket-preview-number">{{ groups.length }} group(s)</span>
            </div>

            <div class="ticket-preview-body gg-list-body">
                <div v-if="!groups.length" class="gg-empty">No guest groups found.</div>

                <div v-for="(group, index) in groups"
                     :key="group.guestGroupId || index"
                     class="gg-list-item"
                     :class="{ 'gg-list-item--last': index === groups.length - 1 }">
                    <div class="gg-list-main">
                        <div class="gg-list-title">{{ group.groupName }}</div>
                        <div class="gg-list-meta"> {{ group.guestCount || 0 }} guest(s) ·  {{ formatMMDDYYYY(group.createdDate) }}</div>
                    </div>
                    <div class="gg-list-actions">
                        <button type="button" class="gg-link gg-link--accent" :disabled="disabled" @click="viewDetails(group)">Details ▼</button>
                        <button type="button" class="gg-link gg-link--success" :disabled="disabled" @click="addGuest(group)">Add Guest</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Guest Group Detail -->
        <div v-else-if="msg.type === 'guest_group_detail'" class="ticket-preview-card">
            <div class="ticket-preview-header">

                <div><h4>{{ detail.groupName || 'Guest Group' }}</h4></div>
            </div>

            <div class="ticket-preview-body">
                <div v-if="!detailGuests.length" class="gg-empty">No guests yet.</div>

                <div v-for="(guest, index) in detailGuests"
                     :key="guest.guestGroupMapId || guest.guestId || index"
                     class="gg-guest-row">
                    <div>
                        <div class="gg-guest-name"> {{ guest.fullName || '—' }}</div>
                        <div class="gg-guest-contact">
                            {{ guest.email || '' }}<span v-if="guest.email && guest.phoneNumber"> · </span>{{ guest.phoneNumber || '' }}
                        </div>
                    </div>
                    <button type="button" class="gg-link gg-link--danger" :disabled="disabled" @click="askRemoveGuest(guest)">Remove</button>
                </div>

                <div class="gg-add-wrap">
                    <button type="button" class="gg-btn gg-btn--secondary" :disabled="disabled" @click="addGuest(detail)">Add Guest</button>
                </div>
            </div>
        </div>

        <!-- Text-only guest-group states -->
        <div v-else-if="textOnlyTypes.includes(msg.type)"
             class="gg-text-message"
             v-html="formatGuestGroupText(responseText)">
        </div>

        <!-- Remove Guest Confirmation -->
        <div v-if="removeGuestModal" class="gg-modal-overlay" @click.self="closeRemoveGuestModal">
            <div class="gg-modal">
                <div class="gg-modal-header">Remove Guest</div>
                <div class="gg-modal-body">
                    Are you sure you want to remove "<strong>{{ selectedGuest.fullName }}</strong>" from this guest group?
                </div>
                <div class="gg-modal-actions">
                    <button type="button" class="gg-modal-btn gg-modal-btn--cancel" @click="closeRemoveGuestModal">Cancel</button>
                    <button type="button" class="gg-modal-btn gg-modal-btn--ok" :disabled="disabled" @click="confirmRemoveGuest">Ok</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Dropdown from 'primevue/dropdown';
    import UserService from '@/service/user.service.js';
    import {
        findDefaultUsCountryCode,
        resolvePhoneLength,
    } from '@/helpers/phoneCountryHelpers.js';

    function formatMMDDYYYY(value) {
        if (!value) return '—';
        const text = String(value).trim();

        const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (iso) return `${iso[2]}-${iso[3]}-${iso[1]}`;

        const date = text.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
        if (date) return `${date[1].padStart(2, '0')}-${date[2].padStart(2, '0')}-${date[3]}`;

        return text;
    }

    export default {
        name: 'ManageGuestGroups',
        components: {
            Dropdown,
        },
        props: {
            msg: { type: Object, required: true },
            disabled: { type: Boolean, default: false },
            currentDate: { type: String, default: '' },
        },
        emits: [
            'confirm-group',
            'cancel',
            'group-detail',
            'add-guest',
            'remove-guest',
            'phone-submit',
        ],
        data() {
            return {
                removeGuestModal: false,
                selectedGuest: {},

                countryCodeList: [],
                phoneForm: {
                    countryCode: '',
                    phoneNumber: '',
                },
                phoneLength: 10,
                phoneError: '',

                textOnlyTypes: [
                    'guest_group_flow',
                    'guest_group_cancelled',
                    'view_guest_groups_empty',
                    'view_guest_groups_failed',
                    'guest_group_detail_failed',
                    'guest_group_guest_delete_failed',
                ],
            };
        },
        async created() {
            await this.GetCountryCodeList();
        },
        computed: {
            payload() {
                return this.msg && this.msg.payload ? this.msg.payload : {};
            },
            guestGroupData() {
                return this.payload.guest_group_data || {};
            },
            prefilled() {
                return this.guestGroupData.prefilled || {};
            },
            prefilledGuests() {
                return Array.isArray(this.prefilled.guests) ? this.prefilled.guests : [];
            },
            groups() {
                return Array.isArray(this.guestGroupData.groups) ? this.guestGroupData.groups : [];
            },
            detail() {
                return this.guestGroupData.detail || {};
            },
            detailGuests() {
                return Array.isArray(this.guestGroupData.guests) ? this.guestGroupData.guests : [];
            },
            resultGuests() {
                return Array.isArray(this.guestGroupData.guests) ? this.guestGroupData.guests : [];
            },
            responseText() {
                return this.payload.response || this.payload.formal_response || this.msg.text || '';
            },
            isGuestPhonePrompt() {
                if (this.msg.type !== 'guest_group_flow') return false;

                const text = String(this.responseText || '')
                    .replace(/<[^>]*>/g, ' ')
                    .replace(/\*\*/g, '')
                    .toLowerCase();

                return text.includes('phone number')
                    || text.includes('phone no')
                    || text.includes('phone');
            },
            isError() {
                return this.msg.type === 'guest_group_failed';
            },
        },
        methods: {
            formatMMDDYYYY,

            formatGuestGroupText(text) {
                if (!text) return '';

                return String(text)
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            },
            formatPhonePrompt(text) {
                return String(text || '')
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            },

            async GetCountryCodeList() {
                try {
                    const response = await UserService.countryList(
                        'countryCode',
                        '00Test',
                        localStorage.getItem('companyId')
                    );

                    this.countryCodeList = response.data || [];

                    const us = findDefaultUsCountryCode(this.countryCodeList)
                        || this.countryCodeList.find((item) =>
                            String(item.text || '').includes('US')
                        );

                    if (us) {
                        this.phoneForm.countryCode = us.value;
                        this.phoneLength =
                            resolvePhoneLength(us.value, this.countryCodeList) || 10;
                    }
                } catch (e) {
                    console.error('Country code load failed', e);
                }
            },

            onGuestCountryCodeChange() {
                this.phoneLength =
                    resolvePhoneLength(
                        this.phoneForm.countryCode,
                        this.countryCodeList
                    ) || 10;

                this.phoneForm.phoneNumber = '';
                this.phoneError = '';
            },

            onGuestPhoneInput(event) {
                const maxLength = this.phoneLength || 10;

                const digits = String(event.target.value || '')
                    .replace(/\D/g, '')
                    .slice(0, maxLength);

                let formatted = digits;

                if (maxLength === 10) {
                    if (digits.length > 6) {
                        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                    } else if (digits.length > 3) {
                        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                    } else if (digits.length > 0) {
                        formatted = `(${digits}`;
                    }
                }

                this.phoneForm.phoneNumber = formatted;
                this.phoneError = '';
            },

            resolveGuestDialCode() {
                const value = this.phoneForm.countryCode;
                if (!value) return '';

                const selected = (this.countryCodeList || [])
                    .find((item) => item.value === value);

                if (!selected || !selected.text) return value;

                const dialCode = String(selected.text).trim().split(/\s+/)[0];
                return dialCode || value;
            },

            submitGuestPhone() {
                this.phoneError = '';

                if (!this.phoneForm.countryCode) {
                    this.phoneError = 'Please select a country code.';
                    return;
                }

                const digits = String(this.phoneForm.phoneNumber || '')
                    .replace(/\D/g, '');

                const requiredLength = this.phoneLength || 10;

                if (digits.length !== requiredLength) {
                    this.phoneError =
                        `Enter a valid ${requiredLength}-digit phone number.`;
                    return;
                }

                const dialCode = this.resolveGuestDialCode();

                let formattedPhone = digits;

                if (requiredLength === 10) {
                    formattedPhone =
                        `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                }

                const fullPhoneNumber = dialCode
                    ? `${dialCode} ${formattedPhone}`
                    : formattedPhone;

                this.$emit('phone-submit', fullPhoneNumber);
            },

            confirmGroup() {
                this.$emit('confirm-group', { ...this.prefilled });
            },
            viewDetails(group) {
                this.$emit('group-detail', group);
            },
            addGuest(group) {
                this.$emit('add-guest', {
                    guestGroupId: group.guestGroupId,
                    groupName: group.groupName,
                });
            },
            askRemoveGuest(guest) {
                if (!guest || !guest.guestGroupMapId) return;
                this.selectedGuest = { ...guest };
                this.removeGuestModal = true;
            },
            closeRemoveGuestModal() {
                this.removeGuestModal = false;
                this.selectedGuest = {};
            },
            confirmRemoveGuest() {
                const guest = { ...this.selectedGuest };
                this.closeRemoveGuestModal();
                this.$emit('remove-guest', {
                    guestGroupMapId: guest.guestGroupMapId || '',
                    guestId: guest.guestId || '',
                    guestGroupId: this.detail.guestGroupId || '',
                    guestName: guest.fullName || '',
                    groupName: this.detail.groupName || '',
                });
            },
        },
    };
</script>

<style scoped>
    .manage-guest-groups {
        width: 100%;
        font-family: Outfit, sans-serif;
    }

    .gg-summary-card,
    .ticket-preview-card {
        background: #fff;
        border-radius: 16px;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
        overflow: hidden;
        font-size: 13px;
        min-width: 300px;
        max-width: 360px;
    }

    .gg-summary-header,
    .ticket-preview-header {
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #fff;
        padding: 14px 18px;
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .gg-error-header {
        background: linear-gradient(135deg, #991b1b, #dc2626) !important;
    }

    .gg-header-icon,
    .tp-icon {
        font-size: 22px;
    }

    .gg-header-title,
    .ticket-preview-header h4 {
        margin: 0;
        font-weight: 700;
        font-size: 14px;
    }

    .gg-header-subtitle,
    .ticket-preview-header p {
        margin: 2px 0 0;
        font-size: 11px;
        opacity: 0.8;
    }

    .gg-summary-body,
    .ticket-preview-body {
        padding: 14px 18px;
    }

    .gg-row,
    .ticket-preview-row {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        border-bottom: 1px solid #f0f0f0;
        padding-bottom: 8px;
        margin-bottom: 8px;
    }

    .gg-label,
    .tp-label {
        color: #888;
        font-size: 11px;
        font-weight: 600;
    }

    .gg-value,
    .tp-value {
        color: #1e2d45;
        font-size: 12px;
        font-weight: 600;
        text-align: right;
    }

    .gg-preview-guest {
        font-size: 12px;
        padding: 5px 0;
        border-bottom: 1px solid #f5f5f5;
    }

    .gg-empty-inline,
    .gg-empty {
        color: #999;
        font-size: 12px;
        text-align: center;
        padding: 10px 0;
    }

    .gg-actions {
        padding: 0 18px 16px;
        display: flex;
        gap: 8px;
    }

    .gg-actions--stacked {
        flex-direction: column;
    }

    .gg-btn {
        border-radius: 10px;
        padding: 10px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
    }

        .gg-btn:disabled,
        .gg-link:disabled,
        .gg-modal-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
            pointer-events: none;
        }

    .gg-btn--primary {
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #fff;
        border: none;
        font-weight: 700;
    }

    .gg-btn--secondary {
        width: 100%;
        background: #f1f5f9;
        color: #1e2d45;
        border: 1.5px solid #cbd5e1;
    }

    .gg-btn--cancel {
        background: none;
        border: none;
        color: #aaa;
        font-size: 12px;
        text-decoration: underline;
    }

    .ticket-preview-number {
        margin-left: auto;
        font-size: 11px;
        font-weight: 600;
    }

    .gg-list-body {
        padding: 0 16px 8px;
    }

    .gg-list-item {
        padding: 10px 0;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 8px;
    }

    .gg-list-item--last {
        border-bottom: none;
    }

    .gg-list-main {
        flex: 1;
    }

    .gg-list-title {
        color: #1e2d45;
        font-size: 13px;
        font-weight: 600;
    }

    .gg-list-meta {
        color: #64748b;
        font-size: 11px;
        margin-top: 3px;
    }

    .gg-list-actions {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 4px;
    }

    .gg-link {
        background: none;
        border: none;
        padding: 0;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
    }

    .gg-link--accent {
        color: #0f66b8;
    }

    .gg-link--success {
        color: #16a34a;
    }

    .gg-link--danger {
        color: #dc2626;
    }

    .gg-guest-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        padding: 7px 0;
        border-bottom: 1px dashed #f0f0f0;
    }

    .gg-guest-name {
        font-size: 12px;
        font-weight: 600;
        color: #1e2d45;
    }

    .gg-guest-contact {
        margin-top: 2px;
        color: #64748b;
        font-size: 10px;
    }

    .gg-add-wrap {
        padding-top: 10px;
    }

    .ticket-preview-error {
        display: flex;
        gap: 8px;
        margin-top: 8px;
        padding: 10px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        color: #dc2626;
        font-size: 12px;
    }

        .ticket-preview-error p {
            margin: 0;
        }

    .ticket-preview-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-top: 1px solid #f0f0f0;
        padding: 10px 18px;
        color: #64748b;
        font-size: 11px;
    }

    .gg-text-message {
        width: fit-content;
        min-width: 0;
        max-width: 330px;
        background: #ffffff;
        color: #333333;
        padding: 12px 16px;
        border-radius: 6px 16px 16px 16px;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        font-weight: 400;
        line-height: 1.5;
        box-sizing: border-box;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        word-break: normal;
        overflow-wrap: break-word;
    }

        /* Phone card needs its own width */
        .gg-text-message:has(.gg-phone-question) {
            width: 330px;
            max-width: 330px;
            padding: 0;
            border-radius: 16px;
            overflow: visible;
        }

    /* Phone question blue header */
    .gg-phone-question {
        width: 100%;
        padding: 13px 16px;
        margin: 0;
        box-sizing: border-box;
        background: linear-gradient( 135deg, #0f66b8 0%, #122341 100% );
        color: #ffffff;
        border-radius: 16px 16px 0 0;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.4;
    }

    /* Country + phone */
    .gg-phone-row {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 16px 16px 10px;
        box-sizing: border-box;
        background: #ffffff;
    }

    .gg-country-dropdown {
        width: 125px;
        min-width: 125px;
        max-width: 125px;
        flex: 0 0 125px;
    }

    .gg-phone-input {
        flex: 1;
        width: 100%;
        min-width: 0;
        height: 40px;
        padding: 8px 10px;
        border: 1px solid #d7dce2;
        border-radius: 8px;
        background: #ffffff;
        color: #1e2d45;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        outline: none;
        box-sizing: border-box;
    }

        .gg-phone-input:focus {
            border-color: #0f66b8;
            box-shadow: 0 0 0 2px rgba(15, 102, 184, 0.12);
        }

    /* Error */
    .gg-phone-error {
        padding: 0 16px;
        margin: 0 0 5px;
        background: #ffffff;
        color: #dc2626;
        font-size: 11px;
    }

    /* Continue */
    .gg-phone-submit {
        width: calc(100% - 32px);
        min-height: 40px;
        margin: 4px 16px 16px;
        border: 0;
        border-radius: 9px;
        background: linear-gradient( 135deg, #0f66b8 0%, #122341 100% );
        color: #ffffff;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
    }

        .gg-phone-submit:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }

    /* PrimeVue Country Dropdown */
    :deep(.gg-country-dropdown.p-dropdown) {
        width: 125px;
        min-width: 125px;
        max-width: 125px;
        height: 40px;
        border: 1px solid #d7dce2;
        border-radius: 8px;
        background: #ffffff;
        box-shadow: none;
    }

    :deep(.gg-country-dropdown .p-dropdown-label) {
        padding: 9px 7px 9px 10px;
        font-size: 13px;
        color: #1e2d45;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    :deep(.gg-country-dropdown .p-dropdown-trigger) {
        width: 28px;
        min-width: 28px;
    }

    /* Country list popup */
    :deep(.p-dropdown-panel) {
        min-width: 180px !important;
        max-width: 240px !important;
        z-index: 99999 !important;
    }

    :deep(.p-dropdown-items-wrapper) {
        max-height: 210px !important;
    }

    :deep(.p-dropdown-item) {
        padding: 8px 10px;
        font-size: 12px;
        white-space: nowrap;
    }

    /* Mobile */
    @media (max-width: 480px) {
        .gg-text-message {
            max-width: 300px;
        }

            .gg-text-message:has(.gg-phone-question) {
                width: 300px;
                max-width: 300px;
            }

        .gg-country-dropdown {
            width: 115px;
            min-width: 115px;
            max-width: 115px;
            flex-basis: 115px;
        }

        :deep(.gg-country-dropdown.p-dropdown) {
            width: 115px;
            min-width: 115px;
            max-width: 115px;
        }
    }

    .gg-modal-overlay {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.5);
    }

    .gg-modal {
        width: 360px;
        max-width: calc(100vw - 30px);
        overflow: hidden;
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
    }

    .gg-modal-header {
        background: #1e2d45;
        color: #fff;
        padding: 14px 18px;
        font-size: 15px;
        font-weight: 700;
    }

    .gg-modal-body {
        padding: 20px 18px;
        color: #1e2d45;
        font-size: 13px;
    }

    .gg-modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 14px 18px;
    }

    .gg-modal-btn {
        border: none;
        border-radius: 6px;
        padding: 8px 16px;
        color: #fff;
        font-weight: 600;
        cursor: pointer;
    }

    .gg-modal-btn--cancel {
        background: #dc2626;
    }

    .gg-modal-btn--ok {
        background: #1e2d45;
    }
</style>
